import { createContext, useState, useEffect, useLayoutEffect, ReactNode, useMemo } from 'react'

// Falls back to useEffect on the server to avoid SSR warnings
const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect

// Utility function to get system theme preference
const getSystemTheme = (): 'light' | 'dark' => {
  if (typeof window === 'undefined') return 'light'
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

// Define the context type interface
interface ThemeContextType {
  currentTheme: 'light' | 'dark'
  setTheme: (theme: 'light' | 'dark' | null) => void
}

export const ThemeContext = createContext<ThemeContextType>({
  currentTheme: 'light',
  setTheme: () => {},
})

interface ThemeProviderProps {
  contentfulDarkModeSet?: boolean
  children: ReactNode
}

export const ThemeProvider = ({ children }: ThemeProviderProps) => {
  // Always initialize with 'light' to match SSR output and avoid hydration mismatches.
  // The actual user preference is applied by useEffect after hydration.
  // CSS dark: classes (applied synchronously by the inline script in app.blade.php)
  // handle backgrounds, text, etc. before React takes over.
  const [currentTheme, setCurrentTheme] = useState<'light' | 'dark'>('light')

  // Apply actual user preference after hydration using a layout effect so the theme
  // is correct before child useEffects run and before the browser paints, preventing
  // a flash of light-mode images in dark mode during SSR direct page access.
  useIsomorphicLayoutEffect(() => {
    const stored = localStorage.getItem('theme')
    if (stored === '"dark"') {
      setCurrentTheme('dark')
    } else if (stored !== '"light"') {
      const system = getSystemTheme()
      if (system === 'dark') setCurrentTheme('dark')
    }
  }, [])

  // Function to update theme and localStorage
  const setTheme = (theme: 'light' | 'dark' | null) => {
    if (theme === null) {
      localStorage.removeItem('theme')
      setCurrentTheme(getSystemTheme())
    } else {
      localStorage.setItem('theme', JSON.stringify(theme))
      setCurrentTheme(theme)
    }
  }

  // Apply theme to DOM immediately without delays
  useEffect(() => {
    document.documentElement.classList.toggle('dark', currentTheme === 'dark')
  }, [currentTheme])

  // handle theme switcher
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key !== 'theme') return

      try {
        const stored = e.newValue
        if (stored === '"dark"') {
          setCurrentTheme('dark')
        } else if (stored === '"light"') {
          setCurrentTheme('light')
        } else if (stored === null) {
          // Theme was cleared, use system preference
          setCurrentTheme(getSystemTheme())
        }
      } catch {
        console.log('Error accessing localStorage')
      }
    }

    // Only listen for storage changes from other tabs
    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [])

  const contextValue = useMemo(
    () => ({
      currentTheme,
      setTheme,
    }),
    [currentTheme, setTheme]
  )

  return <ThemeContext.Provider value={contextValue}>{children}</ThemeContext.Provider>
}
